import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Loader2, Mail, ChevronDown, ChevronUp } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('es-ES', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
};

export const EmailLogHub = ({ authFetch }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API}/admin/emails`);
      setData(await res.json());
    } catch {
      setData({ emails: [], stats: { total: 0, enviados: 0, fallidos: 0 } });
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => { load(); }, [load]);

  const emails = data?.emails || [];
  const stats = data?.stats || { total: 0, enviados: 0, fallidos: 0 };

  return (
    <div className="space-y-5" data-testid="admin-email-log">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h3 className="text-text-heading font-heading text-lg font-semibold flex items-center gap-2">
            <Mail className="w-5 h-5 text-primary" /> Correos enviados
          </h3>
          <p className="text-sm text-text-muted">Todos los emails que el sistema envía (bienvenidas, confirmaciones, notificaciones) con su estado y contenido.</p>
        </div>
        <button type="button" onClick={load} disabled={loading} className="btn-secondary !px-4 !py-2 text-sm inline-flex items-center gap-2 disabled:opacity-50" data-testid="admin-email-log-refresh">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Actualizar
        </button>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="glass-card p-4" data-testid="email-stat-total"><p className="text-2xl font-black text-text-heading">{stats.total}</p><p className="text-xs text-text-muted">Totales</p></div>
        <div className="glass-card p-4" data-testid="email-stat-enviados"><p className="text-2xl font-black text-green-400">{stats.enviados}</p><p className="text-xs text-text-muted">Enviados</p></div>
        <div className="glass-card p-4" data-testid="email-stat-fallidos"><p className="text-2xl font-black text-red-400">{stats.fallidos}</p><p className="text-xs text-text-muted">Fallidos</p></div>
      </div>

      <div className="glass-card overflow-hidden" data-testid="admin-email-log-table">
        <div className="overflow-x-auto">
          <table className="w-full text-base">
            <thead>
              <tr className="border-b border-border-subtle">
                <th className="text-left p-4 text-text-muted font-medium">Fecha</th>
                <th className="text-left p-4 text-text-muted font-medium">Destinatario</th>
                <th className="text-left p-4 text-text-muted font-medium">Asunto</th>
                <th className="text-left p-4 text-text-muted font-medium">Estado</th>
                <th className="text-left p-4 text-text-muted font-medium">Ver</th>
              </tr>
            </thead>
            <tbody>
              {emails.map((mail, i) => {
                const isOpen = expanded === mail.id;
                return (
                  <React.Fragment key={mail.id || i}>
                    <tr className="border-b border-border-subtle/50 hover:bg-card-hover transition-colors cursor-pointer" onClick={() => setExpanded(isOpen ? null : mail.id)} data-testid={`email-log-row-${i}`}>
                      <td className="p-4 text-text-body whitespace-nowrap">{formatDate(mail.created_at)}</td>
                      <td className="p-4 text-primary text-sm">{mail.to}</td>
                      <td className="p-4 text-text-heading font-medium max-w-sm truncate">{mail.subject}</td>
                      <td className="p-4">
                        {mail.success
                          ? <span className="text-xs px-2 py-1 rounded-full bg-green-500/20 text-green-400">Enviado</span>
                          : <span className="text-xs px-2 py-1 rounded-full bg-red-500/20 text-red-400" title={mail.error || ''}>Fallido</span>}
                      </td>
                      <td className="p-4 text-primary">{isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}</td>
                    </tr>
                    {isOpen && (
                      <tr className="border-b border-border-subtle/50 bg-slate-950/40" data-testid={`email-log-detail-${i}`}>
                        <td colSpan={5} className="p-4">
                          {mail.error && <p className="text-xs text-red-400 mb-2">Error: {mail.error}</p>}
                          <p className="text-xs text-text-muted mb-2">De: {mail.from_email}</p>
                          <div className="rounded-xl overflow-hidden border border-border-subtle bg-white">
                            <iframe title={`email-${mail.id}`} sandbox="" srcDoc={mail.html || '<p>Sin contenido</p>'} className="w-full h-96 border-0" />
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        {!loading && emails.length === 0 && <p className="p-8 text-center text-text-muted" data-testid="email-log-empty">Aún no hay correos registrados. A partir de ahora, cada email que envíe el sistema quedará guardado aquí.</p>}
        {loading && !data && <p className="p-8 text-center text-text-muted">Cargando correos…</p>}
      </div>
    </div>
  );
};

export default EmailLogHub;
